import { useEffect, useState } from 'react';
import addIcon from '../../common/Images/add.png';
import { BOOLEAN, DATE_TIME, EXTERNAL, FLAT_RADIO, RADIO, SELECT } from './filterTypes';
import RadioBadge from './RadioBadge';
import Badge from './Badge';
import { RANGE_FILTER_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import Filter from './Filter';
import moment from 'moment';
import { Button, ButtonStyles, Icons } from '@silverfort/sf-components';

const Filters = ({ filters, darkMode, onValueChange, externalHandlers, openOnTop, closeAllFilters, newDesign }) => {
    const [showAdvancedFiltersList, setShowAdvancedFiltersList] = useState();
    const [filterInFocus, setFilterInFocus] = useState('');

    useEffect(() => {
        if (closeAllFilters) {
            setFilterInFocus('');
        }
    }, [closeAllFilters]);
    const clearFilterHandler = (filter) => {
        onValueChange(filter.id, filter.defaultValue || null, filter.isExcluded);
        setFilterInFocus('');
    };

    const doesFilterHasValue = (filter) => {
        if (filter.filterType === BOOLEAN) {
            return filter.value;
        } else
            return Array.isArray(filter.value)
                ? filter.value.length !== 0
                : filter.value !== null && filter.value !== undefined;
    };

    const advancedFilterClickedHandler = (filter) => {
        if (filter.filterType === BOOLEAN) {
            onValueChange(filter.id, true);
        }

        setFilterInFocus(filter.id);
        setShowAdvancedFiltersList(false);
    };

    const filterConfirmedHandler = (filterId, newValue, isExcluded) => {
        let value = null;

        if (newValue && newValue.length > 0) {
            value = newValue;
        }

        onValueChange(filterId, value, isExcluded);
        setFilterInFocus('');
    };

    const clickHandler = (filter) => {
        setShowAdvancedFiltersList(false);
        if (filterInFocus === filter.id) {
            setFilterInFocus('');
        } else if (filter.filterType === BOOLEAN) {
            onValueChange(filter.id, !filter.value);
        } else if (filter.filterType === EXTERNAL) {
            externalHandlers[filter.externalFilterType](filter);
        } else {
            setFilterInFocus(filter.id);
        }
    };

    const buildFilters = (filtersList) => {
        const filters = [];
        filtersList.forEach((filter) => {
            if (filter.filterType === FLAT_RADIO) {
                filters.push(
                    <RadioBadge
                        options={filter.filterConfig.options}
                        checkedValue={filter.value}
                        onChange={(value) => onValueChange(filter.id, value)}
                        id={`radio_${filter.id}`}
                        key={`radio_${filter.id}`}
                        darkMode={darkMode}
                    />
                );
            } else if (filter.filterType === BOOLEAN) {
                filters.push(
                    <div key={filter.id} className="relative inline-block mb-1.5">
                        <Badge
                            newDesign={newDesign}
                            header={filter.label}
                            description={''}
                            hasClear={filter.value}
                            cleared={() => clearFilterHandler(filter)}
                            clicked={() => clickHandler(filter)}
                            include={true}
                            selected={filter.value}
                            darkMode={darkMode}
                        />
                    </div>
                );
            } else {
                const hasClear = filter.value && filter.value.length > 0;
                filters.push(
                    <div key={filter.id} className={'relative inline-block mb-1'}>
                        <Badge
                            header={filter.label}
                            description={getFilterDescription(filter)}
                            hasClear={hasClear}
                            cleared={() => clearFilterHandler(filter)}
                            clicked={() => clickHandler(filter)}
                            include={!filter.isExcluded}
                            darkMode={darkMode}
                            newDesign={newDesign}
                        />
                        {filterInFocus === filter.id && filter.filterType !== EXTERNAL && (
                            <Filter
                                canceled={() =>
                                    filterConfirmedHandler(filter.id, filter.value || null, filter.isExcluded)
                                }
                                confirmed={filterConfirmedHandler}
                                key={filter.id}
                                filter={filter}
                                header={filter.label}
                                filterTooltip={filter.tooltip}
                                darkMode={darkMode}
                                openOnTop={openOnTop}
                                newDesign={newDesign}
                            />
                        )}
                    </div>
                );
            }
        });

        return filters;
    };

    let fixedFilters = [];
    let additionalActiveFilters = [];
    const advancedFilters = [];

    // set filters display order -> fixed, advanced with values, list of advanced without values
    for (const filter of filters) {
        if (filter.isAdvanced) {
            if (doesFilterHasValue(filter) || filter.id === filterInFocus) additionalActiveFilters.push(filter);
            else advancedFilters.push(filter);
        } else fixedFilters.push(filter);
    }

    fixedFilters = buildFilters(fixedFilters);
    additionalActiveFilters = buildFilters(additionalActiveFilters);

    return (
        <div className={`bg-transparent flex text-base`}>
            {!newDesign && (
                <div className="ml-2 mt-1.5 w-[80px] flex items-center">
                    <i
                        className={`fa fa-filter text-lg mb-1.5 mt-0 mx-1 ${
                            darkMode ? 'text-stone-300' : 'text-stone-500'
                        }`}
                        aria-hidden="true"></i>
                    <label className={`mt-0 mx-2 mb-1.5 ${darkMode ? 'text-stone-300' : 'text-stone-500'}`}>
                        Filters
                    </label>
                </div>
            )}
            <div className={'flex items-center font-sf text-sf-gray-800 flex-wrap px-1.5 pt-3 py-0.5'}>
                {fixedFilters}
                {additionalActiveFilters}
                {advancedFilters.length ? (
                    <div className={'relative inline-block mb-1.5'}>
                        {newDesign ? (
                            <Button
                                text="More"
                                iconLeft={Icons.Add}
                                buttonStyle={ButtonStyles.TertiaryBorder}
                                className="!border-sf-gray-100"
                                onClick={() => {
                                    setShowAdvancedFiltersList(!showAdvancedFiltersList);
                                    setFilterInFocus('');
                                }}
                            />
                        ) : (
                            <button
                                className={`border-solid border-sf-primary border px-1.5 py-1 mx-1 text-sm flex items-center justify-center rounded-2xl h-6 w-20 ${
                                    darkMode ? 'text-slate-200' : 'bg-white text-sf-primary'
                                }`}
                                onClick={() => {
                                    setShowAdvancedFiltersList(!showAdvancedFiltersList);
                                    setFilterInFocus('');
                                }}>
                                <img className="mr-1" src={addIcon} alt="" />
                                <span>More</span>
                            </button>
                        )}
                        {showAdvancedFiltersList && (
                            <div
                                className={`${
                                    newDesign ? 'left-[-30px]' : 'left-1 '
                                } flex flex-col absolute bg-white text-base min-w-[170px] max-w-[400px]  m-1.5 rounded z-50 shadow-sm shadow-slate-800 font-sf normal-case text-slate-600`}>
                                {advancedFilters.map((f) => (
                                    <div
                                        key={f.id}
                                        className="px-2 py-1 cursor-pointer w-full border-b border-slate-200 border-solid hover:bg-sky-100"
                                        onClick={() => advancedFilterClickedHandler(f)}>
                                        {f.label}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const dateFilterToText = (filter) => {
    let description;

    if (filter.value[0] !== RANGE_FILTER_TYPES.CUSTOM_RANGE.value) {
        const rangeType = Object.values(RANGE_FILTER_TYPES).find((rt) => rt.value === filter.value[0])?.label;
        description = rangeType;
    } else if (filter.value[1]) {
        description = moment.unix(filter.value[1]).format('MM/DD/YYYY');
        if (filter.value[2]) {
            description += ` - ${moment.unix(filter.value[2]).format('MM/DD/YYYY')}`;
        }
    }

    return description;
};

const multiSelectFilterToText = (filter) => {
    let description;

    const filterItemsCount = filter.value.length;
    if (filterItemsCount === 1) {
        if (filter.filterType === SELECT) {
            const options = [...(filter.filterConfig.commonOptions || []), ...filter.filterConfig.options];
            const matchedOptionLabel = options.find((opt) => opt.value === filter.value[0])?.label;
            description = matchedOptionLabel;
        } else if (filter.filterType === EXTERNAL) {
            description = `${filter.value[0].excluded ? 'not ' : ''}${filter.value[0].label}`;
        } else {
            description = filter.value[0].toString();
        }

        if (filter.isExcluded) description = `not ${description}`;
    } else if (filterItemsCount > 1) {
        description = `${filterItemsCount} Values`;
    }

    return description;
};

export const getFilterDescription = (filter) => {
    let description = filter.value;
    if (filter.filterType === DATE_TIME && filter.value) {
        description = dateFilterToText(filter);
    } else if (filter.filterType === RADIO && !!filter.value) {
        const filterValue = Array.isArray(filter.value) ? filter.value[0] : filter.value;
        description = filter.filterConfig.options.find((option) => option.value === filterValue)?.label;
    } else if (filter.filterType !== BOOLEAN && filter.filterType !== FLAT_RADIO && filter.value) {
        description = multiSelectFilterToText(filter);
    }

    if (!description) {
        description = filter.defaultDescription ? filter.defaultDescription : 'All';
    }

    return description;
};

export default Filters;
